// src/components/UpgradeCtaToast.jsx
//
// Contextual upgrade prompt for free-plan users on Global Search.
//
// Visibility is controlled by the backend `shows_cta` flag (or, as a
// frontend fallback, low remaining unlock tokens). The toast lives in the
// bottom-right corner so it never blocks search results, and stays dismissed
// for the rest of the session once the user closes it.
//
// Pass a `refreshKey` prop that bumps every time an unlock attempt happens
// — that re-fetches the status so the toast can appear immediately after
// the user runs out of tokens, instead of waiting for the next nav.
//
// Pass `shiftLeft` when a right-side panel (e.g. Global Search's activity
// panel) is open so the toast slides left and doesn't visually overlap it.

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

const STATUS_URL =
  "https://whiteintel.io/api/regular/app/v2/user/upgrade_cta_status.php";

const DISMISS_KEY = "wi.upgradeCtaDismissed";

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      ""
    );
  } catch {
    return "";
  }
}

export default function UpgradeCtaToast({ refreshKey = 0, shiftLeft = false }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => {
      try {
        return sessionStorage.getItem(DISMISS_KEY) === "1";
      } catch {
        return false;
      }
    }
  );

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(STATUS_URL, {
          headers: { Authorization: `Bearer ${getJwtToken()}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStatus(data);
      } catch {
        // Silent — this CTA is non-critical, never blow up the page.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dismissed, refreshKey]);

  // Trust the backend's `shows_cta` flag first; fall back to a frontend
  // heuristic so the toast still works while the endpoint is being built.
  const shouldShow =
    !dismissed &&
    status &&
    (status.shows_cta === true ||
      (status.plan === "free" && (status.tokens_remaining ?? 0) <= 5));

  if (!shouldShow) return null;

  const tokensLeft = status.tokens_remaining ?? 0;
  const reason = status.cta_reason;

  // Headline copy adapts to *why* the toast is showing. Out-of-tokens is the
  // sharpest trigger so it gets the most urgent line.
  let headline;
  if (reason === "out_of_tokens" || tokensLeft === 0) {
    headline = "You're out of unlock tokens";
  } else if (reason === "low_tokens" || tokensLeft <= 5) {
    headline = `Only ${tokensLeft} unlock token${tokensLeft === 1 ? "" : "s"} left`;
  } else {
    headline = "Unlock more with a paid plan";
  }

  const onDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    setDismissed(true);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed bottom-5 z-50 w-[320px] max-w-[calc(100vw-2rem)] rounded-md border border-sky-500/30 bg-[#0a1224]/95 backdrop-blur-md shadow-[0_10px_40px_-10px_rgba(56,189,248,0.4)] p-4 transition-[right] duration-200 ${shiftLeft ? "right-[360px]" : "right-5"}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 rounded-md bg-sky-500/15 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-sky-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-white leading-tight">
            {headline}
          </div>
          <p className="mt-1 text-[12px] text-neutral-400 leading-relaxed">
            Upgrade to unlock unlimited credential checks, full result exports, and API access.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/upgrades")}
              className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors"
            >
              View plans
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-md px-2 py-1.5 text-[11px] text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="shrink-0 p-1 rounded text-neutral-500 hover:text-white hover:bg-white/5 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
